// Platform permission catalog — code mirror of the DB `permissions`
// table seeded by migration 0037 (plus follow-ups). Two reasons we
// keep a TS const alongside the DB:
//
//   1. Type-narrowing for requirePermission("sites:read.list") so
//      misspelt permission ids fail at compile time on platform-level
//      scopes (the 100+ enumerated below).
//   2. Audit script (scripts/audit/route-scope-audit.ts) walks the
//      AST and validates that every @requireScope JSDoc tag names a
//      known id.
//
// ─── Module-owned scopes ───
// Module-contributed permissions (e.g. `incident:execute.escalate`)
// are **NOT** listed here anymore — they live in their owning
// module's `modules/<id>/rbac/scopes.ts` and are validated:
//   - at PR time by `audit:modules` (scope id namespacing) +
//     `audit:routes` (which also reads module slices)
//   - at runtime by the DB `permissions` catalog table (seeded via
//     per-module migrations like nexus_control/0041/0042/0043) + the
//     actor's permission set built from nexus_control.role_permissions
//
// requirePermission() / hasPermission() accept `string` (not narrowed
// to Permission) so module-scope callsites compile without lying
// about the literal type. Misspellings on module scopes are still
// caught — by audit:routes (CI) + the runtime permission-set check
// (a typoed scope simply won't be in any role's permission set).
//
// Sync rule: if migration adds / renames / removes a PLATFORM
// permission id, this file MUST be updated in the same PR. Module
// scopes only need to update modules/<id>/rbac/scopes.ts +
// migration; this file untouched. Spec §1.5 acknowledges DB is the
// runtime truth; code const is a developer-experience layer.

export const PERMISSIONS = [
  // ── clients (5) ───────────────────────────────────────────────
  "clients:read.list",
  "clients:read.detail",
  "clients:write.create",
  "clients:write.update",
  "clients:write.delete",

  // ── sites (5) ────────────────────────────────────────────────
  "sites:read.list",
  "sites:read.detail",
  "sites:write.create",
  "sites:write.update",
  "sites:write.delete",
  "sites:write.capture_satellite",

  // ── buildings (5) ────────────────────────────────────────────
  "buildings:read.list",
  "buildings:read.detail",
  "buildings:write.create",
  "buildings:write.update",
  "buildings:write.delete",

  // ── floors (7) ───────────────────────────────────────────────
  "floors:read.list",
  "floors:read.detail",
  "floors:write.create",
  "floors:write.update",
  "floors:write.delete",
  "floors:execute.parse_layout",
  "floors:execute.publish_layout",

  // ── locations (8) ──────────────────────────────────────────
  "locations:read.list",
  "locations:read.detail",
  "locations:write.create",
  "locations:write.update",
  "locations:write.delete",
  "locations:execute.pair_tag",
  "locations:execute.unbind",
  "locations:execute.rebind",

  // ── routes (6) ───────────────────────────────────────────────
  "routes:read.list",
  "routes:read.detail",
  "routes:write.create",
  "routes:write.update",
  "routes:write.delete",
  "routes:execute.reorder",

  // ── tasks (8) ────────────────────────────────────────────────
  "tasks:read.list",
  "tasks:read.detail",
  "tasks:write.create",
  "tasks:write.update",
  "tasks:write.delete",
  "tasks:execute.assign",
  "tasks:execute.unassign",
  "tasks:execute.respond_submission",

  // ── schedules (8) ────────────────────────────────────────────
  "schedules:read.list",
  "schedules:read.detail",
  "schedules:read.self",
  "schedules:write.create",
  "schedules:write.update",
  "schedules:write.delete",
  "schedules:execute.generate_roster",
  "schedules:execute.approve_swap",

  // ── patrols (7) ──────────────────────────────────────────────
  "patrols:read.list",
  "patrols:read.detail",
  "patrols:read.self",
  "patrols:read.evidence",
  "patrols:write.scan",
  "patrols:write.skip",
  "patrols:execute.cancel_session",

  // ── incidents (7) ────────────────────────────────────────────
  "incident:read.list",
  "incident:read.detail",
  "incident:read.self",
  "incident:write.create",
  "incident:write.update",
  "incident:execute.acknowledge",
  "incident:execute.close",

  // ── users (10) ───────────────────────────────────────────────
  "users:read.list",
  "users:read.detail",
  "users:read.officer_status",
  "users:write.invite",
  "users:write.update",
  "users:write.revoke",
  "users:execute.enroll_face",
  "users:execute.enroll_passkey",
  "users:execute.revoke_credentials",
  "users:execute.unbind_line",

  // ── roles (7) — meta-permission for managing RBAC itself ─────
  "roles:read.list",
  "roles:read.detail",
  "roles:write.create",
  "roles:write.update",
  "roles:write.delete",
  "roles:execute.assign_to_user",
  "roles:execute.revoke_from_user",

  // ── billing (5) ──────────────────────────────────────────────
  "billing:read.invoices",
  "billing:read.usage",
  "billing:write.adjust",
  "billing:execute.export_pdf",
  "billing:execute.rollup",

  // ── reports (4) ──────────────────────────────────────────────
  "reports:read.officer",
  "reports:read.site",
  "reports:read.tenant",
  "reports:execute.generate_custom",

  // ── notifications (8) ────────────────────────────────────────
  "notifications:read.logs",
  "notifications:read.rules",
  "notifications:read.groups",
  "notifications:write.create_rule",
  "notifications:write.update_rule",
  "notifications:write.delete_rule",
  "notifications:execute.test_send",
  "notifications:execute.dispatch",

  // ── line (5) ─────────────────────────────────────────────────
  "line:write.update_group",
  "line:write.delete_group",
  "line:execute.bind_group",
  "line:execute.sync_menu",
  "line:execute.reconcile",

  // ── audit (3) ────────────────────────────────────────────────
  "audit:read.log",
  "audit:read.patrols",
  "audit:execute.export",

  // ── ai (5) ───────────────────────────────────────────────────
  //   ai:execute.propose_from_conversation — the LINE AI-understanding capability
  //     (PROPOSE-ONLY, spec docs/specs/line-inbound-contract.md §5.3 / §5.7): read a
  //     bounded, governed, secret-free conversation window from a CLAIMED group and
  //     emit a STRUCTURED PROPOSAL DRAFT (one `proposed` row). It NEVER executes — a
  //     human approves in stage (iii). A SYSTEM-actor permission carried over the
  //     internal entry POST /v1/ai/line/propose; NEVER granted to a human role (same
  //     convention as connect:execute.ingest / runtime:execute.resume_approval). AI
  //     proposes; it never decides or executes a production action.
  "ai:read.usage",
  "ai:read.facts",
  "ai:write.update_preferences",
  "ai:execute.use_chat",
  "ai:execute.propose_from_conversation",

  // ── shared_skills (5) — super_admin platform-tier library ─────
  // Added in P2c migration 0039. Not granted to any system role by
  // default; super_admin holds them via god-mode in verify-session.
  "shared_skills:read.list",
  "shared_skills:read.detail",
  "shared_skills:write.create",
  "shared_skills:write.update",
  "shared_skills:write.delete",

  // ── platform (1) — super_admin operational actions ────────────
  // Added in P2c migration 0039. Only super_admin holds.
  "platform:execute.impersonate",

  // ── mcp (2) — external LLM client governance ────
  // mcp:write.manage_own_keys — added in migration 0049. Granted to every
  //   active tenant role + super_admin (god-mode). Spec
  //   docs/specs/mcp-read-only.md §4.3.
  // mcp:approve_tool_call — authorize a pending MCP tool-call human-approval:
  //   approve / reject a gated high-risk MCP tool-call on the control plane
  //   (runtime-api POST /v1/runtime/mcp-tool-call-approvals/:id/decide). The
  //   approver MUST be a DIFFERENT identity than the actor who requested the
  //   call (four-eyes; runtime-api re-checks this deterministically). The
  //   verdict is the HUMAN's tap recorded verbatim — AI never decides it. A
  //   privileged control-plane scope; assigned to tenant_admin only (least
  //   privilege). Spec docs/specs/nexus-mcp-approval-audit-v0.md §7 / §10. RBAC
  //   seed deferred to go-live / the FORGE flow per NEXUS CLAUDE.md §9 (the
  //   baseline ships schema only, no permission seeds).
  // mcp:manage_projection — manage per-tenant MCP tool-projection OVERRIDES:
  //   create / update / delete the rows that TIGHTEN (hide / context-gate) which
  //   MCP tools a permitted caller sees + may invoke (runtime-api CRUD
  //   /v1/runtime/mcp-tool-projections). An override only NARROWS the deny-by-
  //   default permission baseline (spec §11 merge algebra) — it can never widen
  //   tool exposure past what the caller's RBAC already authorizes. It GOVERNS
  //   TOOL EXPOSURE to external AI, so it is a privileged, control-plane scope;
  //   assigned to tenant_admin only (least privilege). Spec
  //   docs/specs/nexus-mcp-tool-projection-policy-v0.md §3 / §4 / §11. RBAC seed
  //   deferred to go-live / the FORGE flow per NEXUS CLAUDE.md §9 (the baseline
  //   ships schema only, no permission seeds).
  "mcp:write.manage_own_keys",
  "mcp:approve_tool_call",
  "mcp:manage_projection",

  // ── runtime (3) — Operational IR instance execution (Runtime Core) ────
  // The deterministic, AI-free workflow execution layer (runtime-api;
  // runtime obligation #3). Spec
  // docs/specs/operational-ir-d2-instance-execution-plan-v0.md §7. These are
  // code-mirror entries for the audit gate + requirePermission narrowing;
  // RBAC seed data is deferred to go-live / the FORGE flow per NEXUS
  // CLAUDE.md §9 (the baseline ships schema only, no permission seeds).
  //   runtime:read.instances     — inspect a workflow instance + its state history
  //   runtime:execute.instantiate — instantiate a landed workflow definition
  //   runtime:execute.advance     — advance a running instance across a transition
  //   runtime:execute.verify      — D2-c: record a normalised verification
  //     assertion + evidence by invoking the bound Verification-family provider
  //     (the deterministic provider decides the verdict; this scope governs WHO
  //     may trigger the recording, never WHO decides pass/fail — that is the
  //     trust-verification layer, never AI).
  //   runtime:execute.invoke      — D2-d: invoke a bound capability-family
  //     provider (Connect / Communication / Reporting / Data …) and record the
  //     normalised `capability_invocations` outcome. The provider executes; the
  //     conductor records — NEVER a fabricated success. References-only secrets:
  //     the conductor handles NO secret (the provider resolves its own). No
  //     verdict / assurance gate (that is the verification special case); a
  //     required capability that does not succeed fail-closes the gated advance.
  //   runtime:execute.resume_approval — stage D (interactive-approval callback
  //     contract §6 / §10): RESUME a paused instance from an out-of-band approval
  //     tap (POST /v1/runtime/interactions) + cancel a pending approval. The
  //     connector forwards the tap as a system actor; this scope governs WHO may
  //     drive the resume, never WHO decides — the verdict is the HUMAN's tap,
  //     recorded verbatim; the bound-approver authorization is re-checked
  //     deterministically in runtime-api, never by AI.
  // AI proposes; it never decides a guard or a verdict. Provider selection is
  // human-approved at binding time, never AI-finalized.
  "runtime:read.instances",
  "runtime:execute.instantiate",
  "runtime:execute.advance",
  "runtime:execute.verify",
  "runtime:execute.invoke",
  "runtime:execute.resume_approval",

  // ── connect (5) — Connect-family connector runtime (connect-api) ──────
  // The "integrate what exists" track: per-tenant connectors that turn an external
  // system's events into Operational-IR-governed runtime events and perform outbound
  // actions within the permission boundary. Spec docs/specs/connector-runtime.md;
  // upstream FORGE nexus-connector-runtime-contract-v0 / external-event-mapping-v0 /
  // integration-capability-registry-v0. Code-mirror entries for the audit gate +
  // requirePermission narrowing; RBAC seed is deferred to go-live / the FORGE flow.
  //   connect:read.installations — list a tenant's installed connectors + readiness (status only)
  //   connect:write.install      — install a connector kind for the tenant
  //   connect:write.set_secret   — set/rotate a per-tenant connector credential (a HUMAN action;
  //                                AI has no path to write a secret — contract §8)
  //   connect:write.activate     — activate/disable an installation (gated on all secret refs set)
  //   connect:execute.dispatch   — fire an outbound connector action (AI never fires it unattended)
  //   connect:execute.ingest     — ingest an inbound event forwarded over an INTERNAL service
  //                                binding (the LINE path A: notification-api verifies the LINE
  //                                signature once at the single public ingress, then forwards the
  //                                normalised general message to connect-api with a system actor
  //                                carrying THIS permission; not a public route — spec
  //                                docs/specs/line-inbound-contract.md §2.3 / §5.6). A system-actor
  //                                permission; never granted to a human role.
  "connect:read.installations",
  "connect:write.install",
  "connect:write.set_secret",
  "connect:write.activate",
  "connect:execute.dispatch",
  "connect:execute.ingest",
] as const;

/** Literal union of PLATFORM permission ids. Module-contributed
 *  scope strings (e.g. `incident:execute.escalate`) are NOT in this
 *  type — they're validated at runtime via the actor's permission
 *  set + at PR time by audit:modules + audit:routes. Use `Scope`
 *  (alias for `string`) when accepting either platform OR module
 *  scope ids, and use `Permission` only when narrowing to platform
 *  ids matters for type safety. */
export type Permission = typeof PERMISSIONS[number];

/** Either a platform Permission id or a module-contributed scope id.
 *  Functionally identical to `string` — exists to document intent at
 *  the API boundary (requirePermission accepts both kinds). */
export type Scope = Permission | string;

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(s: string): s is Permission {
  return PERMISSION_SET.has(s);
}

export function hasPermission(
  have: readonly string[],
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  const haveSet = new Set(have);
  for (const r of required) if (!haveSet.has(r)) return false;
  return true;
}

export function hasAnyPermission(
  have: readonly string[],
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  const haveSet = new Set(have);
  for (const r of required) if (haveSet.has(r)) return true;
  return false;
}

/** System role ids (immutable templates seeded by 0037 migration).
 *  Used by verify-session to map legacy `tenant_memberships.role_v2`
 *  string to a system role; that mapping bridges old auth flow to the
 *  new permission catalog during the P2a-P2c transition window. */
export const SYSTEM_ROLE_IDS = {
  tenant_admin: "sys_tenant_admin",
  manager: "sys_manager",
  officer: "sys_officer",
  auditor: "sys_auditor",
} as const;

/** Legacy `role_v2` value → system role id mapping. Roles that no
 *  longer exist in spec §3 (site_supervisor / incident_reporter /
 *  control_center_operator / client_viewer) map to the closest
 *  system role so existing logins keep working through the P2
 *  cutover window. */
export const LEGACY_ROLE_TO_SYSTEM_ROLE_ID: Record<string, string> = {
  tenant_admin: "sys_tenant_admin",
  manager: "sys_manager",
  officer: "sys_officer",
  auditor: "sys_auditor",
  // Retired roles — pinned to closest survivor per CL1 decision:
  site_supervisor: "sys_officer",
  incident_reporter: "sys_officer",
  control_center_operator: "sys_manager",
  client_viewer: "sys_auditor",
};
